import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { useAuth } from "../auth";
import { useLocation } from "react-router-dom";
import OverviewRepository from "../../repositories/overview-repository";

interface OverviewData {
  total_unread_inbox: number;
}

interface OverviewContextType {
  data: OverviewData;
  loading: boolean;
  refresh: () => Promise<void>;
}

const context: OverviewContextType = {
  data: {
    total_unread_inbox: 0,
  },
  loading: false,
  refresh: async () => {},
};

const OverviewContext = createContext<OverviewContextType>(context);

export const useOverview = () => useContext(OverviewContext);

export const OverviewProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { state } = useAuth();
  const location = useLocation();
  const [data, setData] = useState<OverviewData>({
    total_unread_inbox: 0,
  });
  const [loading, setLoading] = useState<boolean>(false);

  const loadData = useCallback(async () => {
    if (!state.user || state.phase !== "auth") {
      return;
    }

    setLoading(true);

    try {
      const response = await OverviewRepository.all();
      if (response.data && response.data.success) {
        setData(response.data.data);
      }
    } catch (error) {
      console.error("Failed to fetch overview data", error);
    } finally {
      setLoading(false);
    }
  }, [state.user, state.phase]);

  useEffect(() => {
    if (state.user && state.phase === "auth") {
      loadData().then();
    }
  }, [loadData, state.user, state.phase, location.pathname]);

  return (
    <OverviewContext.Provider
      value={{
        data,
        loading,
        refresh: loadData,
      }}
    >
      {children}
    </OverviewContext.Provider>
  );
};
